<script>
import { mapActions, mapState, mapWritableState } from "pinia/dist/pinia";
import Modal from "@/components/Modal";
import Tabs from "@/components/Tabs";
import EventCard from "@/components/EventCard.vue";
import AddNewSimpleEvent from "./AddNewSimpleEvent.vue";
import { useMainStore } from "../../../../stores/mainStore";
import datesMixins from "../../../../mixins/datesMixins";
import { format } from "../../../../libs/date.format";
import getImageBaseLink from "../../../../components/methods/getImageBaseLink";
import InputField from "@/components/forms/InputField";
import ButtonComponent from "@/components/Button/ButtonComponent.vue";
import { getAgesString } from "../../../../libs/ages.helper.js";
import { ACTIVE_FEATURES, getFireBaseFeatureValue } from
    "../../../../components/methods/getFeatureValue";

const IMAGE_BASE_LINK = getImageBaseLink();
const VenueOverview = {
    name: "VenueOverview",
    components: {
        Modal,
        Tabs,
        EventCard,
        AddNewSimpleEvent,
        InputField,
        ButtonComponent,
    },
    mixins: [datesMixins],
    emits: ["update"],
    props: {
        venue: {
            type: Object,
        },
        allActivities: {
            type: Array,
            // eslint-disable-next-line vue/require-valid-default-prop
            default: [],
        },
        termDates: {
            type: Object,
        },
        sessions: {
            type: Object,
        },
        activityAges: {
            type: Object,
            default: {},
        },
    },
    data() {
        return {
            activeTab: "live",
            showEditModal: false,
            modalVenue: {
                venueName: "",
                address1: "",
                area: "",
                city: "",
                postcode: "",
                country: "United Kingdom",
            },
            modalVenueErrors: {
                venueName: "",
                address1: "",
                area: "",
                city: "",
                postcode: "",
            },
            activeFilter: "All",
            showAddNewEvent: false,
            availableActivity: {
                eventType: "",
                ages: [],
                name: "",
                img: "",
                description: "",
                primaryCategory: [],
                secondaryCategories: [],
                bookingEmail: "",
                bookingType: "",
            },
            showEditModalSpinner: false,
            showDeleteModalSpinner: false,
            isSellingTicketFeatureActive: false,
        };
    },
    methods: {
        ...mapActions(useMainStore, ["getData", "updateVenue", "deleteVenue"]),

        getTickets(session) {
            if (session === null || session === undefined) {
                return null;
            }

            const activity = this.allActivities.find((a) => a.activity_id === session.activity_id);

            // If activity.tickets is set user selected option to sell tickets and added some amount of tickets to sell
            // we need to add functionality how to calculate amount of the available? tickets
            return activity.tickets !== null ? 1 : 0;
        },

        getActTitle(session) {
            if (session === null || session === undefined) {
                return "";
            }

            const activity = this.allActivities.find((a) => a.activity_id === session.activity_id);
            const repetition = session.repetition_type;
            let repeat = "";

            const termDate = this.termDates
                .find((td) => td.termdate_id === session.termdate_id);

            const weekDay = this.getWeekdayFromDate(new Date(termDate.start));

            if (repetition === "day") {
                repeat = "Every day";
            } else if (repetition === "week") {
                repeat = `Every ${weekDay}`;
            } else if (repetition === "month-w") {
                const date = new Date(termDate.start);
                let d = "";
                if (!Number.isNaN(date.getDay())) {
                    d = this.nthWeekdayOfMonth(date.getDay(), date.getDate(), date);
                }
                repeat = Number.isNaN(date.getDay()) ? "" : `Every ${d} ${weekDay}`;
            } else if (repetition === "month-d") {
                repeat = `Every ${format(new Date(termDate.start), "dS")}`;
            }

            if (repetition === "none") {
                repeat = `${weekDay}`;
            }

            return `${repeat} • ${activity.title} `;
        },

        getActAges(session) {
            return getAgesString(this.activityAges[session.activity_id]);
        },

        getSessionDates(session) {
            const termdate = this.termDates.find((td) => td.termdate_id === session.termdate_id);
            // console.log(this.termDates.find((td) => td.termdate_id === session.termdate_id));
            if (termdate.start !== termdate.end) {
                return `${format(termdate.start, "dS mmmm")} - ${format(termdate.end, "dS mmmm yyyy")}`;
            }
            return `${format(termdate.end, "dS mmmm yyyy")}`;
        },

        // TODO: ectract this method for this component and for EventDates component
        nthWeekdayOfMonth(weekday, day, date) {
            let count = 0;
            const dayNumber = ["First", "Second", "Third", "Fourth", "Fifth"];
            const idate = new Date(date.getFullYear(), date.getMonth(), 1);
            while (true) {
                if (idate.getDay() === weekday) {
                    if (idate.getDate() === day) {
                        break;
                    }
                    count += 1;
                }
                idate.setDate(idate.getDate() + 1);
            }

            return dayNumber[count];
        },

        tabChanged(tab) {
            this.activeTab = tab.name;
        },

        async submitModal() {
            const valid = this.validateEditVenueModal();

            if (valid && !this.showEditModalSpinner) {
                this.showEditModalSpinner = true;
                await this.updateVenue(this.modalVenue);

                this.showEditModalSpinner = false;
                this.showEditModal = false;
                await this.getData();
            }
        },

        validateEditVenueModal() {
            this.modalVenueErrors.venueName = this.modalVenue.venueName === ""
                ? "Add venue name"
                : "";

            this.modalVenueErrors.address1 = this.modalVenue.address1 === ""
                ? "Add address"
                : "";

            this.modalVenueErrors.area = this.modalVenue.area === ""
                ? "Add area"
                : "";

            this.modalVenueErrors.city = this.modalVenue.city === ""
                ? "Add city"
                : "";

            this.modalVenueErrors.postcode = this.modalVenue.postcode === ""
                ? "Add postcode"
                : "";

            return Object.values(this.modalVenueErrors).filter((el) => el !== "").length <= 0;
        },

        submitDeleteEditModal() {
            this.showEditModal = false;
            this.showConfirmDeleteVenueModal = true;
        },

        closeEditModal() {
            this.showEditModal = false;
        },

        openEditModal(venue) {
            this.modalVenue = {
                ...this.modalVenue,
                venueName: venue.name,
                address1: venue.address,
                area: venue.county,
                city: venue.city,
                postcode: venue.postcode,
                venueId: venue.venue_id,
            };

            this.showEditModal = true;
        },

        async submitConfirmDeleteVenue() {
            if (!this.showDeleteModalSpinner) {
                this.showDeleteModalSpinner = true;
                const res = await this.deleteVenue(
                    this.$route.params.venueId,
                    this.getVenueActivityIds,
                );
                this.showDeleteModalSpinner = false;
                this.showEditModal = false;
                this.showConfirmDeleteVenueModal = false;
                if (!res.data.success) {
                    alert(res.data.errors);
                } else {
                    this.$router.push({ name: "Simple" });
                }
            }
        },

        getImageForSession(activityId) {
            const activity = this.allActivities.find((a) => a.activity_id === activityId);
            return `${IMAGE_BASE_LINK}${activity.listing_image_name}`;
        },

        getSessionInfo(session) {
            const termDate = this.termDates.find((td) => td.termdate_id === session.termdate_id);

            if (session.repetition_type === "none") {
                const timeString = `${format(termDate.start, "dS mmmm")}`;
                return `${session.time_start} - ${session.time_finish} • ${timeString} • <span>£ ${session.price} per session</span>`;
            }
            const timeString = `${format(termDate.start, "dS mmmm")} - ${format(termDate.end, "dS mmmm yyyy")}`;
            return `${session.time_start} - ${session.time_finish} • ${timeString} • <span>£ ${session.price} per session</span>`;
        },

        filterTabsForSessions(session) {
            const tabs = [];
            // eslint-disable-next-line array-callback-return
            session.map((s) => {
                if (s.repetition_type === "day" && tabs.findIndex((el) => el === "Daily") === -1) {
                    tabs.push("Daily");
                }

                if (s.repetition_type === "week" && tabs.findIndex((el) => el === "Weekly") === -1) {
                    tabs.push("Weekly");
                }

                if (s.repetition_type === "none" && tabs.findIndex((el) => el === "Single event") === -1) {
                    tabs.push("Single event");
                }

                if ((s.repetition_type === "month-w" || s.repetition_type === "month-d") && tabs.findIndex((el) => el === "Other") === -1) {
                    tabs.push("Other");
                }
            });

            return tabs;
        },

        getSessionsForFilter(sessions) {
            // eslint-disable-next-line array-callback-return, consistent-return
            return sessions.filter((s) => {
                if (this.activeFilter === "All") {
                    return true;
                }

                if (this.activeFilter === "Daily") {
                    return s.repetition_type === "day";
                }

                if (this.activeFilter === "Weekly") {
                    return s.repetition_type === "week";
                }

                if (this.activeFilter === "Single event") {
                    return s.repetition_type === "none";
                }

                if (this.activeFilter === "Other") {
                    return s.repetition_type === "month-w" || s.repetition_type === "month-d";
                }
            });
        },

        getImage(session) {
            const activity = this.allActivities.find((a) => a.activity_id === session.activity_id);

            return IMAGE_BASE_LINK + activity.listing_image_name;
        },

        addNewEvent() {
            this.showAddNewEvent = true;
        },

        handleAvailableActivityChange(data) {
            if (data !== null) {
                const activity = this.allActivities.find((a) => a.activity_id === data.activity_id);

                const currentActivityCategories = this.actCategories
                    .filter((ac) => ac.activity_id === data.activity_id);

                const rawPrimaryCategory = currentActivityCategories.find((c) => c.category_type === "0");
                const rawSecondaryCategories = currentActivityCategories.filter((c) => c.category_type === "1").map((c) => c.category_id);

                const primaryCategory = this.allCategories.find(
                    (ac) => ac.category_id === rawPrimaryCategory.category_id,
                );
                let secondaryCategory = [];
                if (rawSecondaryCategories.length > 0) {
                    secondaryCategory = this.allCategories.filter(
                        (ac) => rawSecondaryCategories.indexOf(ac.category_id) !== -1,
                    );
                }

                this.availableActivity = {
                    eventType: activity.event_type,
                    name: "",
                    ages: [],
                    primaryCategory: primaryCategory || [],
                    secondaryCategories: secondaryCategory || [],
                    img: IMAGE_BASE_LINK + activity.listing_image_name,
                    description: activity.description,
                    bookingEmail: activity.booking_email,
                    bookingType: activity.booking_required === "1" ? "required" : "drop-in",
                };
            } else {
                this.availableActivity = {
                    eventType: "",
                    name: "",
                    ages: [],
                    primaryCategory: [],
                    secondaryCategories: [],
                    img: "",
                    description: "",
                    bookingEmail: "",
                    bookingType: "",
                };
            }
        },
    },
    computed: {
        ...mapState(useMainStore, [
            "actCategories",
            "allCategories",
        ]),
        ...mapWritableState(useMainStore, [
            "showConfirmDeleteVenueModal",
        ]),

        getAllTabs() {
            const { live, expired } = this.sessions;

            if (expired.length === 0) {
                return [
                    {
                        name: "live",
                        dataTest: "tab-live",
                        title: `Live events (${this.getSessionsForFilter(live).length})`,
                    },
                ];
            }

            return [
                {
                    name: "live",
                    dataTest: "tab-live",
                    title: `Live events (${this.getSessionsForFilter(live).length})`,
                },
                {
                    name: "expired",
                    dataTest: "tab-expired",
                    title: `Expired events (${this.getSessionsForFilter(expired).length})`,
                },
            ];
        },

        getSessions() {
            const { live, expired } = this.sessions;

            if (this.activeTab === "live") {
                return this.getSessionsForFilter(live);
            }

            return this.getSessionsForFilter(expired);
        },

        emptySessions() {
            const { live, expired } = this.sessions;
            return live.length === 0 && expired.length === 0;
        },

        getListingsAmount() {
            const { live, expired } = this.sessions;

            const actIds = [];

            live.forEach((session) => {
                if (actIds.indexOf(session.activity_id) === -1) {
                    actIds.push(session.activity_id);
                }
            });

            expired.forEach((session) => {
                if (actIds.indexOf(session.activity_id) === -1) {
                    actIds.push(session.activity_id);
                }
            });

            return actIds.length;
        },

        getVenueActivityIds() {
            const { live, expired } = this.sessions;

            const actIds = [];

            live.forEach((session) => {
                if (actIds.indexOf(session.activity_id) === -1) {
                    actIds.push(session.activity_id);
                }
            });

            expired.forEach((session) => {
                if (actIds.indexOf(session.activity_id) === -1) {
                    actIds.push(session.activity_id);
                }
            });

            return actIds;
        },

        getFilters() {
            const { live, expired } = this.sessions;
            let tabs = new Set(
                [...this.filterTabsForSessions(live), ...this.filterTabsForSessions(expired)],
            );

            if (tabs.size > 1) {
                tabs = ["All", ...tabs];
                this.activeFilter = "All";
            } else {
                // eslint-disable-next-line prefer-destructuring
                this.activeFilter = [...tabs][0];
            }

            return tabs;
        },

        getActivitiesForPreSelect() {
            // const { live, expired } = this.sessions;
            //
            // const sessions = [...live, ...expired];
            const activities = this.allActivities.filter(
                (activity) => activity.flow_type === "simple" && activity.status !== "container",
            );

            const unfilteredSessions = activities.map((activity) => {
                const ages = Object.values(this.activityAges[activity.activity_id])
                    ?.map((aa) => aa.map((a) => a.name).join(", ")).join(", ");

                const currentActivityCategories = this.actCategories
                    .filter((ac) => ac.activity_id === activity.activity_id);

                const rawPrimaryCategory = currentActivityCategories.find((c) => c.category_type === "0");
                const rawSecondaryCategories = currentActivityCategories.filter((c) => c.category_type === "1").map((c) => c.category_id);

                const primaryCategory = this.allCategories.find(
                    (ac) => ac.category_id === rawPrimaryCategory.category_id,
                );
                let secondaryCategory = [];
                if (rawSecondaryCategories.length > 0) {
                    secondaryCategory = this.allCategories.filter(
                        (ac) => rawSecondaryCategories.indexOf(ac.category_id) !== -1,
                    );
                }

                return {
                    activityId: activity.activity_id,
                    img: IMAGE_BASE_LINK + activity.listing_image_name,
                    name: activity.title,
                    ages,
                    description: activity.description,
                    eventType: activity.event_type,
                    primaryCategory: primaryCategory.name,
                    secondaryCategories: secondaryCategory.map((c) => c.name).join(", "),
                    bookingEmail: activity.booking_email,
                    bookingType: activity.booking_required === "1" ? "Required" : "Drop in",
                };
            });

            return [...new Map(unfilteredSessions.map((item) => [item.activityId, item])).values()];
        },

        getAvailableActivities() {
            return this.allActivities.filter((activity) => activity.flow_type === "simple")
                .map((activity) => {
                    const ages = this.activityAges[activity.activity_id] !== null
                    && this.activityAges[activity.activity_id] !== undefined
                        ? Object.values(this.activityAges[activity.activity_id])
                            .map((aa) => aa.map((a) => a.name).join(", ")).join(", ")
                        : "";

                    return {
                        title: `${activity.title}  ${ages}`,
                        activity_id: activity.activity_id,
                    };
                });
        },
    },
    async beforeMount() {
        this.isSellingTicketFeatureActive = await getFireBaseFeatureValue(
            ACTIVE_FEATURES.SELLING_TICKET,
        );
    },
};

export default VenueOverview;
</script>

<template>
    <div class="d-flex flex-row">
        <div class="w-100">
            <h1 class="mt-5 mb-2 header-grey font-weight-semibold">Events at {{ venue?.name }}</h1>
            <div class="d-flex flex-row">
                <div class="small-subtitle mr-3">{{ venue?.address }} {{ venue?.city }} {{ venue?.postcode }}</div>
                <ButtonComponent
                    @click="openEditModal(venue)"
                    type="sub"
                    dataTest="btn-edit-venue"
                    class="action-link"
                >
                    Edit
                </ButtonComponent>
            </div>

            <ButtonComponent
                @click="addNewEvent"
                class="add-new-event mt-3"
                size="small"
                dataTest="btn-add-event"
                :testId="'man-pan-sim-add-event-btn'"
            >
                + Add an event
            </ButtonComponent>

            <div class="d-flex flex-row justify-content-between mt-tabs-block">
                <Tabs class="mt-4 tab-container" :tabs="getAllTabs" @change="tabChanged"/>
                <div class="mt-4 d-flex flex-row filter-button-container">
                    <div
                        v-for="(filter, idx) in getFilters"
                        :key="idx"
                        @click="activeFilter = filter"
                        class="filter-button"
                        :class="{'filter-active': filter === activeFilter}"
                    >
                        {{ filter }}
                    </div>
                </div>
            </div>

            <div
                v-for="(session, idx) in getSessions"
                :key="idx"
                class="mt-4"
            >
                <EventCard
                    :title="getActTitle(session)"
                    :ages="getActAges(session)"
                    :dates="getSessionDates(session)"
                    :image="getImage(session)"
                    :venue-id="venue?.venue_id || $route.params.venueId"
                    :tickets="getTickets(session)"
                    :session="session"
                    :simple-flow="true"
                    route-name="Simple event series"
                    :route-params="{
                        venueId: venue?.venue_id || $route.params.venueId,
                        actId: session.activity_id,
                        termId: session.termdate_id,
                        sessionId: session.timetable_id
                    }"
                    :isSellingTicketFeatureActive="isSellingTicketFeatureActive"
                />
            </div>

            <div v-if="emptySessions">
                <div class="venue-container-rounded w-100 d-flex flex-row justify-content-between mt-4 mb-3">
                    <div class="d-flex flex-row">
                        <div>
                            You don't have any events at {{ venue?.name }} name
                        </div>
                    </div>

                    <div>
                        <ButtonComponent
                            @click="addNewEvent"
                            class="add-new-event my-3"
                            dataTest="btn-add-event"
                            size="small"
                        >
                            + Add an event
                        </ButtonComponent>
                    </div>
                </div>
            </div>
        </div>
    </div>

    <Modal
        :show-modal="showEditModal"
        @submit-modal="submitModal"
        @close-modal="closeEditModal"
        @submit-delete="submitDeleteEditModal"
        :new-header="true"
        :header-options="{
            title: 'Edit venue',
        }"
        :show-spinner="showEditModalSpinner"
        submit-text="Update"
        close-text="Delete venue"
        :default-footer="false"
    >
        <div class="mt-4 manual-address-container">
            <div class="mt-4 mb-3 d-flex flex-column">
                <InputField
                    labelText="Venue name"
                    dataTest="input-venue-name"
                    :required="true"
                    v-model="modalVenue.venueName"
                    :value="modalVenue.venueName"
                    :error="modalVenueErrors.venueName"
                />
            </div>
            <div class="mt-4 mb-3 d-flex flex-column">
                <InputField
                    labelText="Address"
                    dataTest="input-venue-address"
                    :maxlength="255"
                    :required="true"
                    v-model="modalVenue.address1"
                    :value="modalVenue.address1"
                    :error="modalVenueErrors.address1"
                />
            </div>
            <div class="mt-4 mb-3 d-flex flex-column">
                <InputField
                    labelText="Area"
                    dataTest="input-venue-area"
                    subLabelText="
                        Please enter an area name.
                        This will help your customers
                        easily differentiate between multiple venues of your business.
                    "
                    :required="true"
                    v-model="modalVenue.area"
                    :value="modalVenue.area"
                    :error="modalVenueErrors.area"
                />
            </div>
            <div class="mt-4 mb-3 d-flex flex-column">
                <InputField
                    labelText="Town/City"
                    dataTest="input-venue-city"
                    :required="true"
                    v-model="modalVenue.city"
                    :value="modalVenue.city"
                    :error="modalVenueErrors.city"
                />
            </div>
            <div class="mt-4 mb-3 d-flex flex-column">
                <InputField
                    labelText="Postcode"
                    dataTest="input-venue-postcode"
                    :required="true"
                    v-model="modalVenue.postcode"
                    :value="modalVenue.postcode"
                    :error="modalVenueErrors.postcode"
                />
            </div>
            <div class="mt-4 mb-3 d-flex flex-column">
                <InputField
                    labelText="Country"
                    dataTest="input-venue-country"
                    :disabled="true"
                    :required="true"
                    v-model="modalVenue.country"
                    :value="modalVenue.country"
                />
            </div>
        </div>
    </Modal>

    <Modal
        v-if="showConfirmDeleteVenueModal"
        :show-modal="showConfirmDeleteVenueModal"
        :show-spinner="showDeleteModalSpinner"
        @close-modal="showConfirmDeleteVenueModal = false"
        @submit-modal="submitConfirmDeleteVenue"
        modal-dialog-class="modal-md"
        close-text="Cancel"
        submit-text="Delete"
        close-btn-class="cancel-link"
        submit-btn-class="delete-button"
    >
        <div class="confirm-modal-spacing">
            Are you sure you want to delete the {{ venue?.name }} {{ venue?.county }} venue?
            This will delete {{ getListingsAmount }} listing(s).
        </div>
    </Modal>

    <AddNewSimpleEvent
        v-if="showAddNewEvent"
        :show-modal="showAddNewEvent"
        :sessions-list="getActivitiesForPreSelect"
        :available-activities="getAvailableActivities"
        :selected-available-activity="availableActivity"
        @close-modal="showAddNewEvent = false"
        @submit-modal="showAddNewEvent = false"
        @change-available-activity="handleAvailableActivityChange"
    />
</template>

<style scoped>
h1 {
    color: #272529;
    font-style: normal;
    font-weight: 600;
    font-size: 2rem;
    line-height: 48px;
}

.header-grey {
    color: #272529;
}

.font-weight-semibold {
    font-weight: 600 !important;
}

.tab-container {
    max-width: 270px;
}

.confirm-modal-spacing {
    margin-top: 3rem;
}

.action-link {
    min-height: 100% !important;
    margin-top: 2px;
}

.mt-tabs-block {
    margin-top: 1.5rem;
}

.filter-button {
    cursor: pointer;
    height: 29px;
    padding: 0 15px;
    padding-top: 5px;
    margin-left: 1rem;
    background: rgba(102, 99, 96, 0.25);
    border-radius: 50px;

    display: flex;
    justify-content: center;
}

.filter-button.filter-active {
    background: #FFB700;
    border-radius: 50px;
    color: #fff;
}

@media only screen and (max-width: 768px) {
    .mt-tabs-block {
        flex-wrap: wrap;
    }

    .filter-button-container {
        flex-wrap: wrap;
        gap: 5px;
    }

    .add-new-event {
        width: 100%;
    }
}

</style>
